// src/pages/scoring/ScoringPage.tsx
import React, { useState, useEffect } from 'react';
import { ScoringSystem } from '../../types';
import BackLink from '../../components/common/BackLink';
import apiService from '../../services/api';

type Tone = 'pos' | 'neg' | undefined;
type Rule = { label: string; value: string; tone?: Tone };

// A rule definition reads one knob off the scoring system and formats its value.
// `show` defaults to "knob is non-zero" so each system renders only its active
// categories — Standard hides the advanced rows; Analytics reveals them.
type RuleDef = {
  label: string;
  field: keyof ScoringSystem;
  tone?: Tone;
  format: (v: number) => string;
};

const pts = (v: number) => `${v > 0 ? '+' : ''}${v} ${Math.abs(v) === 1 ? 'pt' : 'pts'}`;
const perGame = (v: number) => `+${v} / game`;
const mult = (unit: string) => (v: number) => `×${v} / ${unit}`;

const KICKER_RULES: RuleDef[] = [
  { label: 'Field Goal — under 40 yards', field: 'fg_under_40', format: pts },
  { label: 'Field Goal — 40 to 49 yards', field: 'fg_40_49', format: pts },
  { label: 'Field Goal — 50+ yards', field: 'fg_50_plus', format: pts },
  { label: 'Field Goal — 60+ yards', field: 'fg_60_plus', format: pts },
  { label: 'Extra Point', field: 'extra_point', format: pts },
  { label: 'Missed Field Goal', field: 'fg_miss', tone: 'neg', format: pts },
  { label: 'Game-Winning Kick', field: 'game_winner_bonus', tone: 'pos', format: pts },
  { label: 'Clutch Kick — final 2 min', field: 'clutch_kick_bonus', tone: 'pos', format: pts },
  // Advanced (Analytics)
  { label: 'Kickoff Inside the 20', field: 'kickoff_inside_20', tone: 'pos', format: pts },
  { label: 'Kickoff Touchback', field: 'kickoff_touchback', tone: 'neg', format: pts },
  { label: 'Kickoff Out of Bounds', field: 'kickoff_out_of_bounds', tone: 'neg', format: pts },
  { label: 'Onside Kick Recovery', field: 'onside_recovery_bonus', tone: 'pos', format: pts },
  { label: 'FG Expected Points Added (xPA)', field: 'fg_xpa_multiplier', tone: 'pos', format: mult('xPA') },
  { label: 'FG Expected Points Added (EPA)', field: 'fg_epa_multiplier', tone: 'pos', format: mult('EPA') },
];

const PUNTER_RULES: RuleDef[] = [
  { label: 'Punt Inside the 20', field: 'punt_inside_20', format: pts },
  { label: '50+ Yard Punt', field: 'punt_50_plus', format: pts },
  { label: '60+ Yard Punt', field: 'punt_60_plus', format: pts },
  { label: 'Fair Catch Forced', field: 'fair_catch_bonus', format: pts },
  { label: 'Touchback', field: 'touchback_penalty', tone: 'neg', format: pts },
  { label: 'Punt Return TD Allowed', field: 'punt_return_td_penalty', tone: 'neg', format: pts },
  // Advanced (Analytics)
  { label: 'Punt Inside the 10', field: 'punt_inside_10', tone: 'pos', format: pts },
  { label: 'Punt Inside the 5', field: 'punt_inside_5', tone: 'pos', format: pts },
  { label: 'Net Average — 40+ yards', field: 'punt_net_avg_40', tone: 'pos', format: perGame },
  { label: 'Net Average — 45+ yards', field: 'punt_net_avg_45', tone: 'pos', format: perGame },
  { label: 'Punt Expected Points Added (EPA)', field: 'punt_epa_multiplier', tone: 'pos', format: mult('EPA') },
];

function buildRules(system: ScoringSystem, defs: RuleDef[]): Rule[] {
  return defs
    .map((d) => ({ def: d, v: Number(system[d.field] ?? 0) }))
    .filter(({ v }) => v !== 0)
    .map(({ def, v }) => ({ label: def.label, value: def.format(v), tone: def.tone }));
}

function LedgerRow({ label, value, tone }: Rule) {
  const valueColor =
    tone === 'neg' ? 'text-danger-600' : tone === 'pos' ? 'text-success-700' : 'text-secondary-900';
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-secondary-200 last:border-0">
      <span className="text-secondary-800">{label}</span>
      <span className={`font-display text-lg leading-none tabular-nums whitespace-nowrap ${valueColor}`}>
        {value}
      </span>
    </div>
  );
}

function Ledger({ title, sub, rules }: { title: string; sub: string; rules: Rule[] }) {
  return (
    <section>
      <div className="border-b-2 border-ink-900 pb-2 mb-1">
        <h2 className="font-display uppercase text-ink-900 text-2xl leading-none">{title}</h2>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary-500 mt-1">{sub}</p>
      </div>
      <div>
        {rules.map((r) => (
          <LedgerRow key={r.label} {...r} />
        ))}
      </div>
    </section>
  );
}

// Segmented control — record-book style: ink fill on the active segment, sharp corners.
function SystemToggle({
  options,
  selectedId,
  onSelect,
}: {
  options: { id: number; label: string }[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  if (options.length < 2) return null;
  return (
    <div
      role="tablist"
      aria-label="Scoring system"
      className="inline-flex border border-ink-900 divide-x divide-ink-900 self-start"
    >
      {options.map((o) => {
        const active = o.id === selectedId;
        return (
          <button
            key={o.id}
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(o.id)}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] transition-colors ${
              active ? 'bg-ink-900 text-paper' : 'bg-transparent text-ink-900 hover:bg-secondary-100'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// The two real, distinct systems we expose. Power Leg / Precision are seed-clones
// of Standard, so we leave them out of the toggle.
const TOGGLE_SYSTEMS: { name: string; label: string }[] = [
  { name: 'Standard Scoring', label: 'Standard' },
  { name: 'Analytics', label: 'Advanced' },
];

export default function ScoringPage() {
  const [systems, setSystems] = useState<ScoringSystem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesError, setRulesError] = useState(false);

  useEffect(() => {
    apiService.getScoringSystems()
      .then((all) => {
        // Keep only the systems we expose, in toggle order.
        const exposed = TOGGLE_SYSTEMS
          .map((t) => all.find((s) => s.name === t.name))
          .filter((s): s is ScoringSystem => Boolean(s));
        const list = exposed.length ? exposed : all.slice(0, 1);
        setSystems(list);
        const def = list.find((s) => s.is_default) ?? list[0] ?? null;
        setSelectedId(def ? def.id : null);
      })
      .catch(() => setRulesError(true))
      .finally(() => setRulesLoading(false));
  }, []);

  const selected = systems.find((s) => s.id === selectedId) ?? null;
  const isAdvanced = selected?.name === 'Analytics';

  const toggleOptions = systems.map((s) => ({
    id: s.id,
    label: TOGGLE_SYSTEMS.find((t) => t.name === s.name)?.label ?? s.name,
  }));

  const kickerRules = selected ? buildRules(selected, KICKER_RULES) : [];
  const punterRules = selected ? buildRules(selected, PUNTER_RULES) : [];

  return (
    <div className="max-w-5xl">
      {/* Masthead */}
      <BackLink to="/app" className="mb-4">Back to Dashboard</BackLink>
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 border-b-2 border-ink-900 pb-3 mb-8">
        <div>
          <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">Scoring Rules</h1>
          <p className="text-secondary-600 mt-1 max-w-prose">
            {selected?.description ?? 'How kickers and punters earn fantasy points — the official ledger.'}
          </p>
        </div>
        <SystemToggle options={toggleOptions} selectedId={selectedId} onSelect={setSelectedId} />
      </div>

      {rulesLoading ? (
        <div className="flex items-center space-x-2 py-6">
          <div className="loading-spinner w-4 h-4" />
          <span className="text-sm text-secondary-600">Loading rules…</span>
        </div>
      ) : rulesError ? (
        <p className="text-sm text-danger-600 py-6">Could not load scoring rules from the server. Try again shortly.</p>
      ) : selected ? (
        <>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10 mb-10">
            <Ledger title="Kickers" sub="Field goals · extra points · bonuses" rules={kickerRules} />
            <Ledger title="Punters" sub="Placement · distance · coverage" rules={punterRules} />
          </div>

          {/* Strategy — ink-dark callout */}
          <section className="bg-ink-900 text-paper on-ink p-7 sm:p-9">
            <div className="flex flex-wrap items-baseline gap-x-4 mb-5">
              <h2 className="font-display uppercase text-paper text-2xl leading-none">From the Press Box</h2>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Strategy</span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3 text-paper-300 leading-relaxed">
              <li><span className="text-paper font-semibold">Dome kickers</span> stay consistent when the weather turns ugly.</li>
              <li><span className="text-paper font-semibold">High-powered offenses</span> manufacture more scoring chances.</li>
              <li><span className="text-paper font-semibold">Punters on bad teams</span> simply get more chances to punt.</li>
              <li><span className="text-paper font-semibold">Wind and rain</span> swing outdoor leg performance more than anything.</li>
              {isAdvanced && (
                <li>
                  <span className="text-paper font-semibold">Advanced scoring</span> pays pin-deep punting and
                  net average, so a booming, place-it-deep punter is worth nearly as much as a kicker.
                </li>
              )}
            </ul>
          </section>
        </>
      ) : (
        <p className="text-sm text-secondary-500 py-6">No scoring system available.</p>
      )}
    </div>
  );
}
